import { Usb, Plug, X, ExternalLink, Radio, Square } from "lucide-react";

const CH340_WINDOWS_DRIVER_URL =
    "https://www.wch-ic.com/downloads/CH341SER_ZIP.html";

function isWindowsUser() {
    if (typeof navigator === "undefined") return false;

    const platform = navigator.userAgentData?.platform || navigator.platform || "";
    const userAgent = navigator.userAgent || "";

    return /windows|win32|win64/i.test(`${platform} ${userAgent}`);
}

function portLabel(port) {
    if (!port) return "No port selected";
    try {
        const info = port.getInfo();
        const vid = info.usbVendorId
            ? `0x${info.usbVendorId.toString(16).padStart(4, "0")}`
            : "—";
        const pid = info.usbProductId
            ? `0x${info.usbProductId.toString(16).padStart(4, "0")}`
            : "—";
        return `VID ${vid}  PID ${pid}`;
    } catch {
        return "USB Serial Device";
    }
}

export const PortPicker = ({ port, onRequest, onClear, onToggleLogger, loggerActive, disabled, supported }) => {
    if (!supported) {
        return (
            <div
                data-testid="web-serial-unsupported"
                className="border border-[#ef4444]/40 bg-[#ef4444]/5 p-4 text-sm text-[#ededed]"
            >
                <div className="font-semibold mb-1 text-[#ef4444]">
                    Browser not supported
                </div>
                <div className="text-xs text-[#a1a1aa] leading-relaxed font-mono">
                    Use Chrome, Edge, or Opera on desktop. Your current browser
                    cannot communicate with USB devices.
                </div>
            </div>
        );
    }

    if (port) {
        return (
            <div
                data-testid="port-selected"
                className="border border-[#27272a] bg-[#0d0d0e] p-4 flex items-center gap-3"
            >
                <div className="w-9 h-9 flex items-center justify-center bg-[#10B981]/10 border border-[#10B981]/40 shrink-0">
                    <Plug className="w-4 h-4 text-[#10B981]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#a1a1aa]">
                        Port
                    </div>
                    <div
                        className="text-sm font-mono text-[#ededed] truncate"
                        data-testid="port-info-label"
                    >
                        {portLabel(port)}
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        type="button"
                        data-testid="serial-logger-button"
                        disabled={disabled}
                        onClick={onToggleLogger}
                        className={`p-1 transition-colors ${loggerActive ? "text-[#ef4444] hover:text-[#f87171]" : "text-[#a1a1aa] hover:text-[#F59E0B]"} disabled:opacity-50`}
                        aria-label={loggerActive ? "Stop serial logger" : "View serial logger"}
                        title={loggerActive ? "Stop serial logger" : "View serial logger"}
                    >
                        {loggerActive ? <Square className="w-4 h-4 fill-current" /> : <Radio className="w-4 h-4" />}
                    </button>
                    {!disabled && (
                        <button
                            type="button"
                            data-testid="port-clear-button"
                            onClick={onClear}
                            className="text-[#a1a1aa] hover:text-[#ef4444] transition-colors p-1"
                            aria-label="Forget port"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-2">
            <button
                type="button"
                data-testid="select-port-button"
                disabled={disabled}
                onClick={onRequest}
                className="w-full border border-[#27272a] bg-[#0d0d0e] hover:border-[#F59E0B] hover:bg-[#F59E0B]/5 disabled:opacity-50 disabled:cursor-not-allowed p-4 flex items-center gap-3 transition-all text-left"
            >
                <div className="w-9 h-9 flex items-center justify-center border border-[#27272a] shrink-0">
                    <Usb className="w-4 h-4 text-[#a1a1aa]" />
                </div>
                <div className="flex-1">
                    <div className="text-sm text-[#ededed] font-medium">
                        Connect control module
                    </div>
                    <div className="text-xs text-[#a1a1aa] font-mono mt-0.5">
                        Browser will prompt — pick your Hold My Throttle
                    </div>
                </div>
            </button>

            {isWindowsUser() && (
                <div
                    data-testid="windows-ch340-driver-help"
                    className="text-[11px] font-mono text-[#71717a] leading-relaxed"
                >
                    Windows only: if the device picker does not show Hold My
                    Throttle, install the{" "}
                    <a
                        href={CH340_WINDOWS_DRIVER_URL}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[#F59E0B] hover:text-[#fbbf24] underline underline-offset-2"
                    >
                        CH340 USB driver
                        <ExternalLink className="w-3 h-3" aria-hidden="true" />
                    </a>
                    .
                </div>
            )}
        </div>
    );
};
